
import NonAvailabilityGroupEditModal from "./NonAvailabilityGroupEditModal.vue";

export default {
  name: "OfficerAvailabilityCalendar",
  components: { NonAvailabilityGroupEditModal },
  props: {
    baseUrl: { type: String, required: true },
    officerId: { type: Number, required: true },
    officerName: { type: String, default: "" }
  },
  emits: ["close", "saved"],
  data() {
    return {
      currentMonth: this.startOfMonth(new Date()),
      records: [] as any[],
      repeatUntil: "",
      reason: "",
      nonAvailabilityTypes: [] as any[],
      selectedTypeId: null as number | null,
      stagedDates: new Set<string>(),
      editingGroupId: null as string | null,
      loading: false,
      message: "",
      messageType: "",
      weekdayLabels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    };
  },
  computed: {
    selectedTypeRequiresApproval(): boolean {
      const selected = this.nonAvailabilityTypes.find((t: any) => t.nonAvailabilityTypeId === this.selectedTypeId);
      return !!selected && selected.requiresApproval;
    },
    monthLabel(): string {
      return this.currentMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    },
    calendarWeeks(): any[][] {
      const firstOfMonth = this.currentMonth;
      // Monday-first week, matching the WPF calendar's Monday..Sunday header order.
      const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
      const gridStart = new Date(firstOfMonth);
      gridStart.setDate(gridStart.getDate() - firstWeekday);

      const weeks: any[][] = [];
      const cursor = new Date(gridStart);
      for (let w = 0; w < 6; w++) {
        const week: any[] = [];
        for (let d = 0; d < 7; d++) {
          const date = new Date(cursor);
          week.push({
            key: this.formatDateKey(date),
            date,
            day: date.getDate(),
            inCurrentMonth: date.getMonth() === firstOfMonth.getMonth(),
            record: this.findRecordForDate(date)
          });
          cursor.setDate(cursor.getDate() + 1);
        }
        weeks.push(week);
      }
      return weeks;
    }
  },
  watch: {
    officerId: {
      immediate: true,
      handler() {
        this.fetchRecords();
      }
    }
  },
  created() {
    this.fetchNonAvailabilityTypes();
  },
  methods: {
    async fetchNonAvailabilityTypes() {
      try {
        const response = await fetch(`${this.baseUrl}SecurityOfficers/NonAvailability/Types`, {
          method: "GET",
          headers: this.authHeaders()
        });
        if (response.ok) {
          this.nonAvailabilityTypes = await response.json();
        }
      } catch (error) {
        console.error("Error loading non-availability types:", error);
      }
    },
    startOfMonth(date: Date): Date {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    },
    formatDateKey(date: Date): string {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    findRecordForDate(date: Date) {
      const key = this.formatDateKey(date);
      return this.records.find((r: any) => (r.nonAvailabilityDate || "").split("T")[0] === key) || null;
    },
    isPartialDay(record: any): boolean {
      return record.nonAvailabilityStartTime !== "00:00:00" || record.nonAvailabilityEndTime !== "23:59:59";
    },
    cellClass(cell: any) {
      return {
        "calendar-cell-outside": !cell.inCurrentMonth,
        "calendar-cell-unavailable": !!cell.record,
        "calendar-cell-staged": !cell.record && this.stagedDates.has(cell.key)
      };
    },
    previousMonth() {
      this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    },
    nextMonth() {
      this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    },
    authHeaders() {
      const token = localStorage.getItem("token");
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json"
      };
    },
    async fetchRecords() {
      if (!this.officerId) {
        return;
      }
      this.loading = true;
      try {
        const response = await fetch(`${this.baseUrl}SecurityOfficers/NonAvailability/${this.officerId}`, {
          method: "GET",
          headers: this.authHeaders()
        });
        if (response.ok) {
          this.records = await response.json();
        } else {
          this.message = "Failed to load availability.";
          this.messageType = "alert-danger";
        }
      } catch (error) {
        console.error("Error loading non-availability:", error);
        this.message = "Network error while loading availability.";
        this.messageType = "alert-danger";
      } finally {
        this.loading = false;
      }
    },
    onCellClick(cell: any) {
      if (!cell.inCurrentMonth || this.loading) {
        return;
      }
      if (cell.record) {
        this.editingGroupId = cell.record.recurrenceGroupId;
        return;
      }
      this.toggleStagedDate(cell.date);
    },
    toggleStagedDate(date: Date) {
      const key = this.formatDateKey(date);
      if (this.stagedDates.has(key)) {
        this.stagedDates.delete(key);
        return;
      }
      this.stagedDates.add(key);
      if (this.repeatUntil) {
        const [y, m, d] = this.repeatUntil.split("-").map(Number);
        const repeatUntilDate = new Date(y, m - 1, d);
        let next = new Date(date);
        next.setDate(next.getDate() + 7);
        while (next <= repeatUntilDate) {
          this.stagedDates.add(this.formatDateKey(next));
          next = new Date(next);
          next.setDate(next.getDate() + 7);
        }
      }
    },
    clearSelection() {
      this.stagedDates.clear();
    },
    async saveStaged() {
      if (this.stagedDates.size === 0 || !this.selectedTypeId) {
        return;
      }
      this.loading = true;
      this.message = "";
      try {
        const dates = Array.from(this.stagedDates).sort();
        const requiresApproval = this.selectedTypeRequiresApproval;
        const response = await fetch(`${this.baseUrl}SecurityOfficers/NonAvailability/RequestHoliday`, {
          method: "POST",
          headers: this.authHeaders(),
          body: JSON.stringify({
            SecurityOfficerId: this.officerId,
            Dates: dates,
            NonAvailabilityTypeId: this.selectedTypeId,
            Reason: this.reason
          })
        });
        if (response.ok) {
          this.stagedDates.clear();
          this.reason = "";
          this.repeatUntil = "";
          this.message = requiresApproval ? "Request submitted for approval." : "Marked unavailable.";
          this.messageType = "alert-success";
          await this.fetchRecords();
          this.$emit("saved");
        } else {
          this.message = "Failed to save non-availability.";
          this.messageType = "alert-danger";
        }
      } catch (error) {
        console.error("Error saving non-availability:", error);
        this.message = "Network error while saving availability.";
        this.messageType = "alert-danger";
      } finally {
        this.loading = false;
      }
    },
    async onGroupSaved() {
      this.editingGroupId = null;
      await this.fetchRecords();
      this.$emit("saved");
    }
  }
};
